import type { ConversationThread } from '../types';

/**
 * Threads, pure (#765): what a thread is, the objective it sits under, and when
 * it was last active.
 */

/** What a thread is: a route for a conversation, else its kind. */
export type RowKind = 'why' | 'data' | 'doc' | 'agent' | 'group';

export const ROW_KIND_LABEL: Record<RowKind, string> = {
  why: 'Why', data: 'Data', doc: 'Review document', agent: 'Agent chat', group: 'Group',
};

/**
 * The route a conversation took, from what it holds today: a turn that walked
 * (a diagnose result, or a walk still running) is Why, anything else is Data.
 * The router (#760) will record the route itself; until then this is the honest read.
 */
export function rowKind(t: ConversationThread): RowKind {
  if (t.kind === 'doc') return 'doc';
  if (t.kind === 'agent_chat') return 'agent';
  if (t.kind === 'group') return 'group';
  const walked = (t.landingAgentTurns || []).some((turn: any) => !!turn?.result?.diagnose || !!turn?.isWalk || !!turn?.diagnosisOperationId
    || turn?.walked === true);   // a listed turn (#1596): the server read the answer it does not carry
  return walked ? 'why' : 'data';
}

/** The objective (an agent id) a thread belongs to: the one it is scoped to (#766), else an agent chat's source.
 *  Only an objective the viewer can see now groups it; otherwise it is a Recent. */
export function objectiveOf(t: ConversationThread, objectiveIds: ReadonlySet<string>): string | null {
  if (t.objectiveId && objectiveIds.has(t.objectiveId)) return t.objectiveId;
  if (t.sourceKind === 'agent' && t.sourceId && objectiveIds.has(t.sourceId)) return t.sourceId;
  return null;
}

/** When a thread was last active: the later of its last change and its last open. */
export function activeAt(t: ConversationThread): number {
  const changed = new Date(t.updatedAt).getTime();
  const opened = t.lastOpenedAt ? new Date(t.lastOpenedAt).getTime() : NaN;
  const a = Number.isNaN(changed) ? 0 : changed;
  return Number.isNaN(opened) ? a : Math.max(a, opened);
}
